import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Typography, Row, Col, Spin, Space, Divider, Layout } from 'antd';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import Slider from "@mui/material/Slider";
import { runProcessSimulation } from '../../Api/largeDataAnalyticsExternalApi';
import { selectLatestScenario } from '../../Store/slices/largeDataAnalytics.slice';
import ProcessConfigurator from './ProcessConfigurator';
import { GBMConfig, OUConfig, JDConfig } from '../../Configs/ScenarioEngine';
import MetaData from "./MetaData";
import ScenarioProbability from "./ScenarioProbability";
import SimulationPaths from "./SimulationPaths";
import { ArrowRightRounded } from "@mui/icons-material";


const marksDt = [
  { value: 1, label: 'Day' },
  { value: 2, label: 'Week' },
  { value: 3, label: 'Month' },
  { value: 4, label: 'Year' }
];

const mapDtToValue = (value) => {
  switch (value) {
    case 1: return 1/252;
    case 2: return 1/52;
    case 3: return 1/12;
    case 4: return 1;
    default: return 1/252;
  }
};



const ScenarioSimulation = () => {
  const dispatch = useDispatch();
  const [visible, setVisible] = useState(false);
  const [activeConfig, setActiveConfig] = useState(null);
  const [activeProcessName, setActiveProcessName] = useState('');
  const [loading, setLoading] = useState(false);
  const [T, setT] = useState(1);
  const [dt, setDt] = useState(1);

  const configs = {
    gbm: GBMConfig,
    ou: OUConfig,
    jd: JDConfig
  };

  const gbmResult = useSelector(state => selectLatestScenario(state, 'gbm'));
  const ouResult = useSelector(state => selectLatestScenario(state, 'ou'));
  const jdResult = useSelector(state => selectLatestScenario(state, 'jd'));

  const showModal = (processName) => {
    setActiveConfig(configs[processName]);
    setActiveProcessName(processName);
    setVisible(true);
  };

  const handleOk = () => {
    setVisible(false);
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const handleSubmit = (config, values) => {
    const mappedParams = Object.entries(values).reduce((acc, [key, value]) => {
      const mappedKey = config.mapParams[key] || key;
      acc[mappedKey] = value;
      return acc;
    }, {});

    if ('mu' in mappedParams) {
      mappedParams.mu = mappedParams.mu / 100;
    }
    if ('sigma' in mappedParams) {
      mappedParams.sigma = mappedParams.sigma / 100;
    }
    setLoading(true);
    dispatch(runProcessSimulation({
      process_name: activeProcessName,
      processParams: mappedParams,
      T,  // You can make these configurable if needed
      dt: mapDtToValue(dt),
    })).then((resp)=>{
      setLoading(false)
    })
    setVisible(false);
  };

  return (
      <>
        <Row wrap={false}>
          <Col flex={'0 0 200px'} className={'p-4'}>
            <Space direction={'vertical'}>
              <Space onClick={()=>showModal('gbm')}>
                <Typography.Text strong>Geometric Brownian Motion (GBM)</Typography.Text>
                <ArrowRightRounded/>
              </Space>
              <Divider/>
              <Space onClick={ ()=>showModal('ou') }>
                <Typography.Text strong>Ornstein-Uhlenbeck (OU)</Typography.Text>
                <ArrowRightRounded/>
              </Space>
              <Divider/>
              <Space onClick={()=>showModal('jd')}>
                <Typography.Text strong>Jump Diffusion (JD)</Typography.Text>
                <ArrowRightRounded/>
              </Space>
              <Divider/>
              <Space direction={'vertical'}>
                <Typography.Text>Years (T)</Typography.Text>
                <Slider
                  defaultValue={1}
                  valueLabelDisplay="auto"
                  step={1}
                  marks
                  min={1}
                  max={20}
                  sx={{width: 300}}
                  value={T}
                  onChange={(e, value) => setT(value)}
                />
              </Space>
              <Divider/>
              <Space direction={'vertical'}>
                <Typography.Text>Period (dt)</Typography.Text>
                <Slider
                  defaultValue={1}
                  valueLabelDisplay="auto"
                  step={1}
                  marks={marksDt}
                  min={1}
                  max={4}
                  value={dt}
                  sx={{width: 300}}
                  onChange={(e, value) => setDt(value)}
                />
              </Space>
            </Space>
          </Col>
          <Col flex={'1 1 800px'} className={'px-4'}>
            {loading && <Space><Spin /> <Typography.Text>Running Scenarios (may take up to 20 seconds)</Typography.Text></Space>}
            {gbmResult && <ResultDisplay result={gbmResult} />}
            {ouResult && <ResultDisplay result={ouResult} />}
            {jdResult && <ResultDisplay result={jdResult} />}
          </Col>
        </Row>
        <Modal
          title={`Configure ${activeProcessName.toUpperCase()} Process`}
          onOk={handleOk}
          onCancel={handleCancel}
          open={visible}
          footer={[
            <Button key="back" onClick={handleCancel}>
              Cancel
            </Button>,
            <Button key="submit" type="primary" onClick={() => handleSubmit(activeConfig, activeConfig.initialValues)}>
              Run Simulation
            </Button>,
          ]}
        >
          {activeConfig && <ProcessConfigurator config={activeConfig} onSubmit={(values) => handleSubmit(activeConfig, values)} />}
        </Modal>
      </>

  );
};

const ResultDisplay = ({ result }) => {
  if (!result) return null;
  console.log(result);

  return (
    <Row wrap={false}>
      <Col flex={'0 0 300px'}  style={{background: 'var(--light-grey)', borderRadius: 8, maxHeight: '80vh', overflowY: 'auto'}} className={'border-sm py-3'}>
        <MetaData process={result.process} finalParams={result.final_params} config={result.config} />
        <ScenarioProbability analysis={result.analysis} />
      </Col>
      <Col flex={'auto'}>
        <div style={{padding: 50}}>
          <SimulationPaths paths={result.sample_paths} title={result.process} />
        </div>
      </Col>
    </Row>
  );
};

export default ScenarioSimulation;
